import { useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { StatusBadge } from "@/components/shared/StatusBadge";
import * as Icons from "lucide-react";
import { text, toDate, fmtTs } from "@/lib/format";
import { proCompletion } from "@/lib/proCompletion";

const dayKey = (v) => { const d = toDate(v); return d ? d.toLocaleDateString("en-CA") : "Unknown date"; };
const dayLabel = (k) => (k === "Unknown date" ? k : new Date(k + "T00:00:00").toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" }));

// New onboardings grouped by creation date with completion % and pending fields.
export function OnboardingsByDate({ pros, onOpen }) {
  const groups = useMemo(() => {
    const m = {};
    pros.forEach((p) => { (m[dayKey(p.created_at)] ||= []).push(p); });
    return Object.entries(m).sort(([a], [b]) => b.localeCompare(a))
      .map(([k, list]) => [k, list.sort((a, b) => (toDate(b.created_at)?.getTime() || 0) - (toDate(a.created_at)?.getTime() || 0))]);
  }, [pros]);

  if (!pros.length) return <Card className="p-10 text-center text-sm text-muted-foreground">No onboardings in this period.</Card>;
  return (
    <div className="space-y-5" data-testid="onboardings-by-date">
      {groups.map(([k, list]) => {
        const submitted = list.filter((p) => p.onboarding_status === "submitted").length;
        return (
          <Card key={k} data-testid={`onb-day-${k}`}>
            <div className="px-4 py-3 border-b border-border flex flex-wrap items-center justify-between gap-2">
              <p className="font-heading font-semibold text-foreground flex items-center gap-2"><Icons.CalendarDays className="h-4 w-4 text-primary" /> {dayLabel(k)}</p>
              <p className="text-xs text-muted-foreground font-mono">{list.length} new · {submitted} submitted · {list.filter((p) => p.status === "active").length} active</p>
            </div>
            <div className="divide-y divide-border">
              {list.map((p) => <OnbRow key={p.id} p={p} onOpen={onOpen} />)}
            </div>
          </Card>
        );
      })}
    </div>
  );
}

function OnbRow({ p, onOpen }) {
  const c = proCompletion(p);
  return (
    <div className="px-4 py-3 grid grid-cols-1 md:grid-cols-[1.4fr_1fr_0.9fr_1fr_2fr_auto] gap-3 items-center hover:bg-accent/30" data-testid={`onb-row-${p.id}`}>
      <div className="min-w-0">
        <p className="font-medium text-foreground truncate">{text(p.full_name || p.business_name, "Unnamed")}</p>
        <p className="text-[11px] font-mono text-muted-foreground">{text(p.phone)} · {fmtTs(p.created_at)}</p>
      </div>
      <div className="min-w-0"><p className="text-sm truncate">{text(p.category_name)}</p><p className="text-[11px] text-muted-foreground truncate">{text(p.city)} · by {text(p.created_by_role)}</p></div>
      <div className="flex flex-wrap gap-1"><StatusBadge status={p.status} />{p.onboarding_status && <StatusBadge status={p.onboarding_status} />}</div>
      <div className="flex items-center gap-2" data-testid={`onb-completion-${p.id}`}><Progress value={c.pct} className="h-1.5" /><span className="text-xs font-mono w-9 text-right">{c.pct}%</span></div>
      <div className="flex flex-wrap gap-1" data-testid={`onb-missing-${p.id}`}>
        {c.missing.length === 0 ? <span className="text-[11px] text-emerald-700">Complete</span>
          : c.missing.map((m) => <span key={m} className="text-[10px] rounded-full border border-amber-200 bg-amber-50 text-amber-800 px-2 py-0.5">{m}</span>)}
      </div>
      <Button variant="ghost" size="sm" onClick={() => onOpen(p.id)} data-testid={`onb-review-${p.id}`}>Review <Icons.ChevronRight className="h-4 w-4 ml-1" /></Button>
    </div>
  );
}
